/**
 * DecisionFlow — TierPricing
 * ui/src/components/platform-admin/TierPricing.jsx
 * OI-SEAT-IMPLEMENTATION · S660
 *
 * Tier band editor. Reads GET /api/v1/admin/tier-config.
 * Saves via PATCH /api/v1/admin/tier-config/{tier_code}.
 *
 * Response shape (GET):
 *   { tiers: { essentials: { tier_code, tier_name, min_positions,
 *               max_positions, price_per_seat_usd, ... }, ... } }
 *   (or flat array — handled by normalise())
 *
 * PATCH body: { min_positions, max_positions, price_per_seat_usd }
 *
 * Design rules (§15):
 *   - No dark: prefix
 *   - rgb(var(--color-*)) throughout
 *   - .skeleton for loading states
 */

import { useState, useEffect, useCallback } from "react";
import { Edit2, Check, X, RefreshCw } from "lucide-react";

import Card from "../shared/Card.jsx";
import Button from "../shared/Button.jsx";

const API_BASE = "/api/v1";

const TIER_ORDER = ["essentials", "standard", "enterprise"];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function LoadingSkeleton() {
  return (
    <div className="space-y-3 py-2">
      {[0, 1, 2].map((i) => (
        <div key={i} className="h-20 w-full rounded skeleton" />
      ))}
    </div>
  );
}

function ErrorNote({ message }) {
  return (
    <div className="rounded-xl bg-[rgb(var(--color-surface-muted))] px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">
      {message ?? "Unable to load tier configuration."}
    </div>
  );
}

function SuccessNote({ message }) {
  return (
    <div className="rounded-xl bg-[rgb(var(--color-success))]/10 border border-[rgb(var(--color-success))]/30 px-3 py-2 text-tok-sm text-[rgb(var(--color-success))]">
      {message}
    </div>
  );
}

function Field({ label, children, hint }) {
  return (
    <div className="space-y-1">
      <label className="block text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">
        {label}
      </label>
      {children}
      {hint && (
        <p className="text-tok-xs text-[rgb(var(--color-text-muted))] opacity-70">{hint}</p>
      )}
    </div>
  );
}

function NumericInput({ value, onChange, disabled, placeholder }) {
  return (
    <input
      type="number"
      value={value}
      onChange={onChange}
      disabled={disabled}
      placeholder={placeholder}
      className="w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--color-primary))] disabled:opacity-50"
    />
  );
}

// ---------------------------------------------------------------------------
// TierRow — single tier edit card
// ---------------------------------------------------------------------------

function TierRow({ tier, onSaved }) {
  const [editing,  setEditing]  = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState(null);
  const [success,  setSuccess]  = useState(null);

  // Editable fields
  const [minPos,   setMinPos]   = useState(String(tier.min_positions ?? ""));
  const [maxPos,   setMaxPos]   = useState(tier.max_positions != null ? String(tier.max_positions) : "");
  const [price,    setPrice]    = useState(String(tier.price_per_seat_usd ?? ""));

  function handleEdit() {
    // Reset to current DB values
    setMinPos(String(tier.min_positions ?? ""));
    setMaxPos(tier.max_positions != null ? String(tier.max_positions) : "");
    setPrice(String(tier.price_per_seat_usd ?? ""));
    setError(null);
    setSuccess(null);
    setEditing(true);
  }

  function handleCancel() {
    setEditing(false);
    setError(null);
  }

  async function handleSave() {
    setError(null);
    const min = parseInt(minPos, 10);
    const max = maxPos.trim() === "" ? null : parseInt(maxPos, 10);
    const pr  = parseFloat(price);

    if (isNaN(min) || min < 1) {
      setError("Min positions must be a positive integer.");
      return;
    }
    if (max !== null && (isNaN(max) || max < min)) {
      setError("Max positions must be greater than or equal to min positions.");
      return;
    }
    if (isNaN(pr) || pr <= 0) {
      setError("Price per seat must be a positive number.");
      return;
    }

    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/admin/tier-config/${tier.tier_code}`, {
        method:      "PATCH",
        headers:     { "Content-Type": "application/json" },
        credentials: "include",
        body:        JSON.stringify({
          min_positions:      min,
          max_positions:      max,
          price_per_seat_usd: pr,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.detail?.error?.message ?? `Error ${res.status}`);
      }
      setSuccess("Saved.");
      setEditing(false);
      onSaved();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  const tierLabels = {
    essentials: { label: "Essentials",    colour: "text-[rgb(var(--color-primary))]" },
    standard:   { label: "Standard Plus", colour: "text-[rgb(var(--color-success))]" },
    enterprise: { label: "Enterprise",    colour: "text-[rgb(var(--color-warning))]" },
  };
  const meta = tierLabels[tier.tier_code] ?? { label: tier.tier_code, colour: "text-[rgb(var(--color-text))]" };

  return (
    <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-4 space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className={`text-tok-base font-bold ${meta.colour}`}>{meta.label}</p>
          <p className="text-tok-xs font-mono text-[rgb(var(--color-text-muted))]">{tier.tier_code}</p>
        </div>
        {!editing && (
          <button
            onClick={handleEdit}
            className="flex items-center gap-1.5 rounded-lg border border-[rgb(var(--color-border))] px-3 py-1.5 text-tok-xs font-medium text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))] transition"
          >
            <Edit2 className="h-3.5 w-3.5" /> Edit
          </button>
        )}
      </div>

      {/* Read view */}
      {!editing && (
        <div className="grid grid-cols-3 gap-3">
          <div>
            <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Min seats (floor)</p>
            <p className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">
              {tier.min_positions ?? "—"}
            </p>
          </div>
          <div>
            <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Max seats (ceiling)</p>
            <p className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">
              {tier.max_positions ?? <span className="text-[rgb(var(--color-text-muted))]">Unlimited</span>}
            </p>
          </div>
          <div>
            <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">List price / rep / mo</p>
            <p className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">
              ${tier.price_per_seat_usd != null ? Number(tier.price_per_seat_usd).toFixed(2) : "—"}
            </p>
          </div>
        </div>
      )}

      {/* MRR range hint */}
      {!editing && tier.min_positions && tier.price_per_seat_usd && (
        <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
          MRR range: ${(tier.min_positions * tier.price_per_seat_usd).toLocaleString()}
          {tier.max_positions
            ? ` – $${(tier.max_positions * tier.price_per_seat_usd).toLocaleString()}`
            : "+"
          }
          /month
        </p>
      )}

      {/* Success flash */}
      {success && !editing && <SuccessNote message={success} />}

      {/* Edit view */}
      {editing && (
        <div className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Min positions (floor)" hint="Commercial minimum — sales enforced">
              <NumericInput
                value={minPos}
                onChange={(e) => setMinPos(e.target.value)}
                disabled={saving}
                placeholder="e.g. 25"
              />
            </Field>
            <Field
              label="Max positions (ceiling)"
              hint={tier.tier_code === "enterprise" ? "Leave blank for no ceiling" : "Product ceiling — soft gate enforced"}
            >
              <NumericInput
                value={maxPos}
                onChange={(e) => setMaxPos(e.target.value)}
                disabled={saving}
                placeholder={tier.tier_code === "enterprise" ? "Unlimited" : "e.g. 75"}
              />
            </Field>
            <Field label="Price per seat / month (USD)">
              <NumericInput
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                disabled={saving}
                placeholder="e.g. 69.00"
              />
            </Field>
          </div>

          {error && <ErrorNote message={error} />}

          <div className="flex items-center gap-2">
            <Button onClick={handleSave} disabled={saving}>
              <Check className="h-3.5 w-3.5" />
              {saving ? "Saving…" : "Save"}
            </Button>
            <Button variant="secondary" onClick={handleCancel} disabled={saving}>
              <X className="h-3.5 w-3.5" /> Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// TierPricing
// ---------------------------------------------------------------------------

export default function TierPricing() {
  const [tiers,   setTiers]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  const fetchTiers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/admin/tier-config`, {
        credentials: "include",
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.detail?.error?.message ?? `Error ${res.status}`);
      }
      const data = await res.json();
      // Normalise: API may return { tiers: {...} } or flat object
      const raw = data.tiers ?? data;
      setTiers(raw);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTiers(); }, [fetchTiers]);

  // Build ordered array from map
  const tierArray = tiers
    ? TIER_ORDER.map((code) => tiers[code]).filter(Boolean)
    : [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-tok-base font-bold text-[rgb(var(--color-text))]">
            Tier Pricing & Bands
          </h2>
          <p className="mt-0.5 text-tok-xs text-[rgb(var(--color-text-muted))]">
            Changes take effect immediately. DB is the single source of truth — no hardcoded defaults.
          </p>
        </div>
        <Button variant="secondary" onClick={fetchTiers} disabled={loading}>
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {loading && <LoadingSkeleton />}
      {error   && <ErrorNote message={error} />}

      {!loading && !error && tierArray.length === 0 && (
        <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
          No tier configuration found. Verify <span className="font-mono">ref_subscription_tier</span> is seeded.
        </p>
      )}

      {!loading && !error && tierArray.length > 0 && (
        <div className="space-y-3">
          {tierArray.map((tier) => (
            <TierRow key={tier.tier_code} tier={tier} onSaved={fetchTiers} />
          ))}
        </div>
      )}

      {!loading && !error && tierArray.length > 0 && (
        <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-3 text-tok-xs text-[rgb(var(--color-text-muted))] space-y-1">
          <p><span className="font-semibold text-[rgb(var(--color-text))]">Floor</span> — commercial minimum enforced by sales process and tenant contract. Not product-enforced.</p>
          <p><span className="font-semibold text-[rgb(var(--color-text))]">Ceiling</span> — product-enforced via soft gate at position import. Over-limit positions load as inactive.</p>
          <p><span className="font-semibold text-[rgb(var(--color-text))]">Price</span> — list price only. Custom pricing per tenant is set via tenant contract (S661).</p>
        </div>
      )}
    </div>
  );
}
